gsap.registerPlugin(ScrollTrigger);

const largeDesktopAnim = '(min-width: 960px)',
      smallDesktopAnim = '(max-width: 959px) and (min-width: 620px)',
      mobileAnim = '(max-width: 619px)';
const mm = gsap.matchMedia();

const mvmtRotation = 10;
const mvmtTime = 15;
const headingShift = 10;
const imageRotation = 25;

mm.add(largeDesktopAnim, () => {

  gsap.defaults({ ease: "power2.inOut", duration: .2 });
  gsap.set("html", { "--line-color": "#aece36" });
  gsap.set([".cafe-color-block",".snack-color-block"], { backgroundColor: "rgba(255,255,255,0)" });
  gsap.set("h2", { color: "#414042" });
  gsap.set(".copy-wrapper", { opacity: 0, height: 0 });
  // No opacity here (front image just scales in from 0, matching the accent
  // image below) -- opacity fading looked unintentional once this became a
  // scrubbable animation, since you'd see it pass through a half-faded state
  // sitting at an arbitrary mid-scale.
  gsap.set([".cafe-image",".snack-image"], { scale: 0, height: "100%" });
  gsap.set(".cafe-image", { rotate: -imageRotation });
  gsap.set(".snack-image", { rotate: imageRotation });
  gsap.set([".cafe-accent",".snack-accent"], { scale: 0, height: "100%"  });
  gsap.set([".cafe-category-ingredients-wrapper",".snack-category-ingredients-wrapper"], { scale: 1.05, opacity: 0 });

  let mvmt = gsap.timeline({ paused: true });
  mvmt.to([".cafe-category-ingredients-wrapper > *",".snack-category-ingredients-wrapper > *"], {
    ease: "sine.inOut",
    repeat: -1,
    yoyo: true,
    duration: mvmtTime,
    rotate: function(i, elem, boxes) {
      return i % 2 === 1 ? -mvmtRotation : mvmtRotation;
    }
  });

  let mvmt2 = gsap.timeline({ paused: true });
  mvmt2.to([".cafe-accent",".snack-accent"], {
    ease: "sine.inOut",
    repeat: -1,
    yoyo: true,
    duration: mvmtTime/2,
    rotate: function(i, elem, boxes) {
      return i % 2 === 1 ? -mvmtRotation/2 : mvmtRotation/2;
    }
  });

  mvmt.progress(0.9999);
  mvmt2.progress(0.25);

  // One timeline per side drives EVERY visual change together -- heading scale,
  // copy reveal, text color, background wash, and image reveal -- all on the
  // same 0-1 range, so they land in sync instead of drifting apart the way the
  // old separate timelines (textColor, cafeImage/snackImage) did.
  const timelines = {};
  // The actual .trigger.is--left / .trigger.is--right elements, so zone math
  // can be measured against their real on-screen box instead of the whole
  // hero's width.
  const triggerEls = {};
  // The h2 elements, so the shared heading-scale/color logic below can
  // address both of them directly rather than each side's own timeline
  // reaching over to touch the other one (see note further down).
  const headingEls = {};
  // Whether each side is currently fully triggered. Read by computeProgress()
  // below to decide how wide that side's active zone should be -- this is the
  // "lock" that makes a triggered edge take more effort to scrub away.
  const locked = { left: false, right: false };
  // The last progress value we set for each side. Read by updateHeadings()
  // below so it always has both sides' current state, regardless of which
  // side's setProgress call triggered the update.
  const sideProgress = { left: 0, right: 0 };

  gsap.utils.toArray(".trigger").forEach(trigger => {
    let wrapper = trigger.querySelector(".wrapper"),
        copyWrapper = trigger.querySelector(".copy-wrapper"),
        heading = trigger.querySelector("h2"),
        headingWrapper = trigger.querySelector(".title-wrapper"),
        arrow = trigger.querySelector(".arrow"),
        isLeftTrigger = trigger.classList.contains("is--left"),
        side = isLeftTrigger ? "left" : "right",
        colorBlock = isLeftTrigger ? ".cafe-color-block" : ".snack-color-block",
        // Background wash color now comes from the section's color_picker
        // settings (left_bg_color / right_bg_color), passed through via a
        // data-bg-color attribute on the color block element in the Liquid
        // template. The hex fallback here only applies if that attribute is
        // ever missing/blank (e.g. an older cached render).
        accentColor = document.querySelector(colorBlock)?.dataset.bgColor || (isLeftTrigger ? "#AECE36" : "#DA96C1"),
        revealImage = isLeftTrigger ? ".cafe-image" : ".snack-image",
        revealAccent = isLeftTrigger ? ".cafe-accent" : ".snack-accent",
        ingredientsWrapper = isLeftTrigger ? ".cafe-category-ingredients-wrapper" : ".snack-category-ingredients-wrapper",
        // NOTE: the resting CSS default is max-width:45ch. This value MUST be
        // larger than that at every breakpoint -- it's meant to widen the
        // wrapper on reveal, and "34ch" here was a bug: smaller than the 45ch
        // resting value, so revealing at this breakpoint was actually
        // shrinking the wrapper, forcing extra line-wraps that overflowed the
        // copyWrapperNaturalHeight measured below (the clipped button).
        revealMaxWidth = window.innerWidth < 960 ? "52ch" : "60ch",
        tl = gsap.timeline({ paused: true, defaults: { ease: "power2.out", duration: 1 } });

    // Measure copyWrapper's real natural height ONCE, up front, instead of
    // handing GSAP the string "auto". GSAP's height:"auto" tweens revert the
    // CSS property back to the literal "auto" keyword the instant they hit
    // 100% progress (so the box stays responsive to future content changes)
    // -- at that instant the browser recalculates natural height fresh, and
    // if it differs at all from what GSAP had been interpolating toward
    // (sub-pixel rounding, font metrics, whatever), you get a visible snap
    // right at completion. Animating to an explicit pixel number instead
    // sidesteps that revert-to-auto behavior entirely. copyWrapper's own
    // width is never animated (see note below), so one measurement here is
    // valid for the whole tween.
    function measureCopyWrapperNaturalHeight() {
      const restingHeight = copyWrapper.style.height;
      copyWrapper.style.height = "auto";
      const natural = copyWrapper.offsetHeight;
      copyWrapper.style.height = restingHeight;
      return natural;
    }
    let copyWrapperNaturalHeight = measureCopyWrapperNaturalHeight();

    // NOTE: the wrap-width lock attempted here previously (measuring
    // headingWrapper's resting offsetWidth and pinning max-width to it) was
    // unreliable -- the heading's wrap width still changed. Forcing a single
    // line via CSS `white-space: nowrap` on #hero h2 (custom.css.liquid) is
    // the reliable fix instead: it removes the possibility of a mid-hover
    // reflow entirely, since there's no wrap point left to shift.

    // NOTE: heading scale and color used to live in this timeline too (both
    // this side's own heading AND, via a "notHeading"/blanket "h2" selector,
    // the OPPOSITE side's heading). That meant each heading was controlled by
    // TWO different timelines at once -- and since we force-reset the
    // inactive side's timeline to progress(0) on every single mousemove tick
    // while the other side is active, that reset kept re-applying rest-state
    // styles to a heading the ACTIVE side's timeline was simultaneously
    // trying to animate. Two timelines racing over one element is what caused
    // the scale to snap between values on quick switching, and is also why
    // the color tween only reliably applied to "your own" heading. Heading
    // scale/color are now handled entirely by updateHeadings() below instead,
    // which is the single, authoritative place that touches both headings.
    tl.to(headingWrapper, { yPercent: 0 }, 0)
      // Widening the wrapper as it reveals means the heading (and the copy
      // block, which inherits available space from it) wrap less than they
      // would at the resting-state width. NOTE: we deliberately do NOT also
      // animate copyWrapper's own max-width -- that width feeds directly
      // into the natural-height measurement above, so if it changed mid-tween
      // the cached height target would go stale too.
      .to(wrapper, { padding: (window.innerWidth < 960 ? "0 2rem" : "0 4rem"), maxWidth: revealMaxWidth }, 0)
      .to(arrow, { opacity: 0 }, 0)
      .to(copyWrapper, { id: "copyHeight", opacity: 1, height: copyWrapperNaturalHeight }, 0)
      .to(colorBlock, { backgroundColor: accentColor }, 0)
      .to(revealImage, { scale: 1, rotation: 0, y: isLeftTrigger ? "10%" : "0%" }, 0)
      .to(revealAccent, { scale: isLeftTrigger ? .9 : 1, yPercent: isLeftTrigger ? -15 : 0 }, .05)
      .to(ingredientsWrapper, { scale: 1, opacity: .25 }, 0);

    // If the window is resized narrower AFTER this height was captured
    // (still within this same breakpoint's range, so matchMedia doesn't
    // rebuild anything), the cached target goes stale: less horizontal
    // room means the copy wraps onto more lines, but the tween keeps
    // animating to the old, now-too-short pixel height, clipping the
    // button. Re-measuring and pushing the fresh value onto the SAME
    // tween (via its id) keeps future reveals sized correctly without
    // rebuilding the whole timeline.
    function refreshCopyWrapperHeight() {
      copyWrapperNaturalHeight = measureCopyWrapperNaturalHeight();
      const heightTween = tl.getById("copyHeight");
      if (heightTween) {
        heightTween.vars.height = copyWrapperNaturalHeight;
        heightTween.invalidate();
      }
    }

    // Reveal ("in", target 1) and release ("out", target 0) get different
    // durations now that this is a toggle -- reveal-in slowed down per
    // request, release-out kept snappy. quickTo doesn't support different
    // durations per direction on one persistent tween, so this is a small
    // hand-rolled equivalent: a single tracked tween that's only killed and
    // recreated when the TARGET actually changes (not on every mousemove
    // call with the same target), which keeps it just as cheap as quickTo
    // was for the common case of the cursor sitting still on one side of
    // the toggle line.
    const revealInDuration = 0.6;
    const revealOutDuration = 0.25;
    let scrubTween = null;
    let scrubTarget = 0;
    const scrub = (target) => {
      if (target === scrubTarget && scrubTween) return;
      scrubTarget = target;
      if (scrubTween) scrubTween.kill();
      scrubTween = gsap.to(tl, {
        progress: target,
        duration: target === 1 ? revealInDuration : revealOutDuration,
        ease: "power2.out",
        overwrite: true
      });
    };

    timelines[side] = { tl, scrub, refreshHeight: refreshCopyWrapperHeight };
    triggerEls[side] = trigger;
    headingEls[side] = heading;

    trigger.addEventListener("focusin", () => setProgress(side, 1));
    trigger.addEventListener("focusout", () => setProgress(side, 0));
  });

  // Same "< 960" caveat as revealMaxWidth above: this whole block only runs
  // for the 620-959px range, so this isn't just for narrow phones.
  const headingGrowScale = window.innerWidth < 960 ? 2.5 / 1.5 : 2 / 1.5;
  const headingShrinkScale = window.innerWidth < 960 ? 1.5 / 1.5 : 1 / 1.5;

  const headingRestColor = "#414042";
  const headingActiveColor = "white";
  const lineRestColor = "#aece36";
  const lineActiveColor = "white";
  // tl's children (colorBlock, revealImage, copyWrapper, etc.) all share
  // tl's own default ease (power2.out) applied on top of tl's linear
  // progress -- so tl.progress()===0.5 does NOT mean those properties are
  // 50% blended, it means they're power2.out(0.5) blended. Reading lp/rp
  // straight into a LINEAR interpolate() for heading color/scale put those
  // on a different curve than everything else sharing the same tl, so they
  // visibly landed at different points for the same progress value (e.g.
  // background color already well underway while heading color lagged
  // behind). Running lp/rp through this same ease function before
  // interpolating keeps every property -- background, images, and heading
  // scale/color -- on the exact same curve.
  const tlEase = gsap.parseEase("power2.out");

  // The single source of truth for both headings' scale and color, and the
  // shared --line-color variable.
  //
  // Reads each side's LIVE tl.progress() rather than the raw, unsmoothed
  // sideProgress value. tl.progress() is being continuously eased toward
  // the mouse-driven target by entry.scrub's quickTo (power2.out, .25s) --
  // the exact same curve driving the wrapper/copy/image reveal -- so
  // reading it here means the heading scale/color track that identical
  // eased motion instead of jumping straight to the raw cursor position.
  // Previously this read sideProgress directly, which had no easing of its
  // own at all: the heading would snap immediately to wherever the mouse
  // was while the rest of the reveal was still smoothly catching up,
  // which is what made it feel disconnected from everything else.
  function updateHeadings() {
    const lp = timelines.left ? timelines.left.tl.progress() : 0;
    const rp = timelines.right ? timelines.right.tl.progress() : 0;
    // ROOT CAUSE FOUND: gsap.quickTo() targeting the "scale" transform
    // property on these h2 elements is what froze the page -- confirmed by
    // isolation, not just frequency/throttling (we tried direct calls,
    // gsap.ticker, and requestAnimationFrame coalescing; all froze with
    // quickTo). Swapping to a plain, immediate gsap.set() -- same element,
    // same property, no persistent tween object -- does not freeze.
    // transformOrigin set explicitly here (not left to the CSS
    // transform-origin rule) because GSAP's CSS plugin takes ownership of
    // transform-origin once it starts writing "scale" to an element, and can
    // override the CSS-authored value with its own default (center) --
    // left/right scaling from the bottom corner needs to be set through GSAP
    // directly to be reliable.
    //
    // Color is now interpolated directly from the same lp/rp progress used
    // for scale, instead of snapping at an anyActive threshold -- this
    // restores the original scrub-tied behavior (color eases in step with
    // the reveal, in both directions) from before that threshold logic was
    // introduced. It's safe to compute a fresh interpolated value every
    // frame here because gsap.set() is a plain, immediate style write, not
    // a tween -- there's no per-call tween-creation cost like gsap.to() had.
    const elp = lp > 0 ? tlEase(lp) : 0;
    const erp = rp > 0 ? tlEase(rp) : 0;
    gsap.set(headingEls.left, {
      transformOrigin: "left bottom",
      scale: lp > 0 ? gsap.utils.interpolate(1, headingGrowScale, elp)
        : rp > 0 ? gsap.utils.interpolate(1, headingShrinkScale, erp)
        : 1,
      color: lp > 0 ? gsap.utils.interpolate(headingRestColor, headingActiveColor, elp)
        : rp > 0 ? gsap.utils.interpolate(headingRestColor, headingActiveColor, erp)
        : headingRestColor
    });
    gsap.set(headingEls.right, {
      transformOrigin: "right bottom",
      scale: rp > 0 ? gsap.utils.interpolate(1, headingGrowScale, erp)
        : lp > 0 ? gsap.utils.interpolate(1, headingShrinkScale, elp)
        : 1,
      color: rp > 0 ? gsap.utils.interpolate(headingRestColor, headingActiveColor, erp)
        : lp > 0 ? gsap.utils.interpolate(headingRestColor, headingActiveColor, elp)
        : headingRestColor
    });
    const maxEasedProgress = Math.max(elp, erp);
    gsap.set("html", {
      "--line-color": maxEasedProgress > 0
        ? gsap.utils.interpolate(lineRestColor, lineActiveColor, maxEasedProgress)
        : lineRestColor
    });
  }

  // Zones are expressed as a fraction of that SIDE'S OWN .trigger element
  // width (the orange box in the debug overlay) rather than the whole hero,
  // so they visually line up with it instead of floating at some unrelated
  // fraction of the page. The mousemove listener still lives on #hero (below)
  // rather than on each trigger, so cross-side resets and the locked-zone
  // widening keep working even once the cursor leaves that element's box.
  //
  // - edge padding (outer ~35% of the trigger's width, ~60% once locked):
  //   right at the literal edge, doesn't toggle the animation at all --
  //   locking in grows THIS zone (eating into the dead zone), not the scrub
  //   zone, so it takes a deliberate move back toward center to release
  // - scrub zone: progress interpolates with cursor position here. Wider
  //   (baseScrubZoneRatio) on the way IN for a smoother approach; narrower
  //   (lockedScrubZoneRatio) on the way back OUT once locked, for a quicker release
  // - everything past that, toward the middle of the hero, is dead: nothing animates
  // Red (held/padding) zone set to exactly 60% of the trigger's width
  // before triggering, 80% once locked.
  const basePaddingRatio = 0.60;
  const lockedPaddingRatio = 0.80;
  const baseScrubZoneRatio = 0.30;
  const lockedScrubZoneRatio = 0.20;

  function setProgress(side, progress) {
    const entry = timelines[side];
    if (!entry) return;
    entry.scrub(progress);
    sideProgress[side] = progress;
    // IMPORTANT: "locked" should only change at the two extremes -- flip TRUE
    // once progress reaches 1, flip FALSE once it reaches 0 -- and stay put
    // for everything in between. Previously it unset the instant progress
    // dropped even slightly below 1, which meant the very first frame of
    // scrubbing OUT switched the zone math back to the narrower unlocked
    // ratios mid-exit. That put the cursor's current position outside that
    // now-much-smaller active zone, snapping progress straight to 0 instead
    // of scrubbing gradually through the (wider, locked) orange band.
    if (progress >= 1) {
      locked[side] = true;
    } else if (progress <= 0) {
      locked[side] = false;
    }
    if (progress > 0) {
      if (mvmt.paused()) { mvmt.play(); mvmt2.play(); }
      const other = side === "left" ? "right" : "left";
      if (timelines[other]) { timelines[other].tl.progress(0); locked[other] = false; sideProgress[other] = 0; }
    } else if (!locked.left && !locked.right) {
      mvmt.pause();
      mvmt2.pause();
    }
  }

  // Runs updateHeadings() every animation frame instead of once per
  // setProgress() call. Since updateHeadings now reads each side's LIVE
  // tl.progress() (see note above it), calling it only when the mouse moves
  // meant the heading would freeze at its last snapshot the moment the
  // cursor stopped, even while tl.progress() kept easing toward its target
  // for up to another .25s -- a visible desync right at the tail end of
  // every movement. A ticker callback keeps it continuously in sync
  // instead. This is safe to run every frame regardless of activity because
  // updateHeadings only calls gsap.set() (a plain, immediate style write),
  // never gsap.quickTo() -- quickTo was what caused the earlier freeze when
  // driven from a ticker; a bare gsap.set() read+write was confirmed safe
  // even at raw mousemove frequency.
  gsap.ticker.add(updateHeadings);

  function computeProgress(side, mouseX) {
    const el = triggerEls[side];
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const isLeftSide = side === "left";
    const distanceFromOuterEdge = isLeftSide ? (mouseX - rect.left) : (rect.right - mouseX);
    const isLocked = locked[side];
    const paddingRatio = isLocked ? lockedPaddingRatio : basePaddingRatio;
    const scrubZoneRatio = isLocked ? lockedScrubZoneRatio : baseScrubZoneRatio;
    const paddingZone = rect.width * paddingRatio;
    const scrubZone = rect.width * scrubZoneRatio;
    // TOGGLE instead of scrub: rather than continuously tracking cursor
    // position across the scrub zone, this now flips fully on/off the
    // instant the cursor crosses the MIDDLE of the scrub zone. The reveal
    // still animates smoothly (entry.scrub's quickTo eases toward whichever
    // target this returns over .25s) -- only the trigger point is now a
    // single line instead of a continuous range.
    //
    // Locked automatically gets more buffer before toggling out "for free"
    // here: paddingZone/scrubZone already switch to the wider
    // lockedPaddingRatio/lockedScrubZoneRatio above when isLocked is true,
    // so toggleThreshold shifts further from the edge (deeper into the
    // trigger) whenever a side is locked, exactly like the old scrub zone
    // did -- just collapsed to its midpoint instead of a range.
    const toggleThreshold = paddingZone + scrubZone / 2;
    return distanceFromOuterEdge < toggleThreshold ? 1 : 0;
  }

  const hero = document.querySelector("#hero");

  const handleHeroMouseMove = (e) => {
    setProgress("left", computeProgress("left", e.clientX));
    setProgress("right", computeProgress("right", e.clientX));
  };

  const handleHeroMouseLeave = () => {
    setProgress("left", 0);
    setProgress("right", 0);
  };

  hero.addEventListener("mousemove", handleHeroMouseMove);
  hero.addEventListener("mouseleave", handleHeroMouseLeave);

  // Keeps each trigger's cached copyWrapperNaturalHeight in sync if the
  // window is resized narrower/wider without crossing this breakpoint's
  // boundaries (matchMedia only tears this whole block down and rebuilds
  // it when the breakpoint itself changes, not on every resize within it).
  const handleResize = () => {
    Object.values(timelines).forEach((entry) => entry.refreshHeight && entry.refreshHeight());
  };
  window.addEventListener("resize", handleResize);

  // GSAP calls this automatically when this breakpoint's media query stops
  // matching (e.g. the window gets resized past it), so listeners and
  // timelines from the previous breakpoint don't linger and stack up.
  return () => {
    hero.removeEventListener("mousemove", handleHeroMouseMove);
    hero.removeEventListener("mouseleave", handleHeroMouseLeave);
    window.removeEventListener("resize", handleResize);
    gsap.ticker.remove(updateHeadings);
    mvmt.kill();
    mvmt2.kill();
    Object.values(timelines).forEach(({ tl }) => tl.kill());
  };


});

mm.add(smallDesktopAnim, () => {

  gsap.defaults({ ease: "power2.inOut", duration: .2 });
  gsap.set("html", { "--line-color": "#aece36" });
  gsap.set([".cafe-color-block",".snack-color-block"], { backgroundColor: "rgba(255,255,255,0)" });
  gsap.set("h2", { color: "#414042" });
  gsap.set(".copy-wrapper", { opacity: 0, height: 0 });
  // No opacity here (front image just scales in from 0, matching the accent
  // image below) -- opacity fading looked unintentional once this became a
  // scrubbable animation, since you'd see it pass through a half-faded state
  // sitting at an arbitrary mid-scale.
  gsap.set([".cafe-image",".snack-image"], { scale: 0, height: "100%" });
  gsap.set(".cafe-image", { rotate: -imageRotation });
  gsap.set(".snack-image", { rotate: imageRotation });
  gsap.set([".cafe-accent",".snack-accent"], { scale: 0, height: "100%"  });
  gsap.set([".cafe-category-ingredients-wrapper",".snack-category-ingredients-wrapper"], { scale: 1.05, opacity: 0 });

  let mvmt = gsap.timeline({ paused: true });
  mvmt.to([".cafe-category-ingredients-wrapper > *",".snack-category-ingredients-wrapper > *"], {
    ease: "sine.inOut",
    repeat: -1,
    yoyo: true,
    duration: mvmtTime,
    rotate: function(i, elem, boxes) {
      return i % 2 === 1 ? -mvmtRotation : mvmtRotation;
    }
  });

  let mvmt2 = gsap.timeline({ paused: true });
  mvmt2.to([".cafe-accent",".snack-accent"], {
    ease: "sine.inOut",
    repeat: -1,
    yoyo: true,
    duration: mvmtTime/2,
    rotate: function(i, elem, boxes) {
      return i % 2 === 1 ? -mvmtRotation/2 : mvmtRotation/2;
    }
  });

  mvmt.progress(0.9999);
  mvmt2.progress(0.25);

  // One timeline per side drives EVERY visual change together -- heading scale,
  // copy reveal, text color, background wash, and image reveal -- all on the
  // same 0-1 range, so they land in sync instead of drifting apart the way the
  // old separate timelines (textColor, cafeImage/snackImage) did.
  const timelines = {};
  // The actual .trigger.is--left / .trigger.is--right elements, so zone math
  // can be measured against their real on-screen box instead of the whole
  // hero's width.
  const triggerEls = {};
  // The h2 elements, so the shared heading-scale/color logic below can
  // address both of them directly rather than each side's own timeline
  // reaching over to touch the other one (see note further down).
  const headingEls = {};
  // Whether each side is currently fully triggered. Read by computeProgress()
  // below to decide how wide that side's active zone should be -- this is the
  // "lock" that makes a triggered edge take more effort to scrub away.
  const locked = { left: false, right: false };
  // The last progress value we set for each side. Read by updateHeadings()
  // below so it always has both sides' current state, regardless of which
  // side's setProgress call triggered the update.
  const sideProgress = { left: 0, right: 0 };

  gsap.utils.toArray(".trigger").forEach(trigger => {
    let wrapper = trigger.querySelector(".wrapper"),
        copyWrapper = trigger.querySelector(".copy-wrapper"),
        heading = trigger.querySelector("h2"),
        headingWrapper = trigger.querySelector(".title-wrapper"),
        arrow = trigger.querySelector(".arrow"),
        isLeftTrigger = trigger.classList.contains("is--left"),
        side = isLeftTrigger ? "left" : "right",
        colorBlock = isLeftTrigger ? ".cafe-color-block" : ".snack-color-block",
        // Background wash color now comes from the section's color_picker
        // settings (left_bg_color / right_bg_color), passed through via a
        // data-bg-color attribute on the color block element in the Liquid
        // template. The hex fallback here only applies if that attribute is
        // ever missing/blank (e.g. an older cached render).
        accentColor = document.querySelector(colorBlock)?.dataset.bgColor || (isLeftTrigger ? "#AECE36" : "#DA96C1"),
        revealImage = isLeftTrigger ? ".cafe-image" : ".snack-image",
        revealAccent = isLeftTrigger ? ".cafe-accent" : ".snack-accent",
        ingredientsWrapper = isLeftTrigger ? ".cafe-category-ingredients-wrapper" : ".snack-category-ingredients-wrapper",
        // NOTE: the resting CSS default is max-width:45ch. This value MUST be
        // larger than that at every breakpoint -- it's meant to widen the
        // wrapper on reveal, and "34ch" here was a bug: smaller than the 45ch
        // resting value, so revealing at this breakpoint was actually
        // shrinking the wrapper, forcing extra line-wraps that overflowed the
        // copyWrapperNaturalHeight measured below (the clipped button).
        revealMaxWidth = window.innerWidth < 960 ? "52ch" : "60ch",
        tl = gsap.timeline({ paused: true, defaults: { ease: "power2.out", duration: 1 } });

    // Measure copyWrapper's real natural height ONCE, up front, instead of
    // handing GSAP the string "auto". GSAP's height:"auto" tweens revert the
    // CSS property back to the literal "auto" keyword the instant they hit
    // 100% progress (so the box stays responsive to future content changes)
    // -- at that instant the browser recalculates natural height fresh, and
    // if it differs at all from what GSAP had been interpolating toward
    // (sub-pixel rounding, font metrics, whatever), you get a visible snap
    // right at completion. Animating to an explicit pixel number instead
    // sidesteps that revert-to-auto behavior entirely. copyWrapper's own
    // width is never animated (see note below), so one measurement here is
    // valid for the whole tween.
    function measureCopyWrapperNaturalHeight() {
      const restingHeight = copyWrapper.style.height;
      copyWrapper.style.height = "auto";
      const natural = copyWrapper.offsetHeight;
      copyWrapper.style.height = restingHeight;
      return natural;
    }
    let copyWrapperNaturalHeight = measureCopyWrapperNaturalHeight();

    // NOTE: the wrap-width lock attempted here previously (measuring
    // headingWrapper's resting offsetWidth and pinning max-width to it) was
    // unreliable -- the heading's wrap width still changed. Forcing a single
    // line via CSS `white-space: nowrap` on #hero h2 (custom.css.liquid) is
    // the reliable fix instead: it removes the possibility of a mid-hover
    // reflow entirely, since there's no wrap point left to shift.

    // NOTE: heading scale and color used to live in this timeline too (both
    // this side's own heading AND, via a "notHeading"/blanket "h2" selector,
    // the OPPOSITE side's heading). That meant each heading was controlled by
    // TWO different timelines at once -- and since we force-reset the
    // inactive side's timeline to progress(0) on every single mousemove tick
    // while the other side is active, that reset kept re-applying rest-state
    // styles to a heading the ACTIVE side's timeline was simultaneously
    // trying to animate. Two timelines racing over one element is what caused
    // the scale to snap between values on quick switching, and is also why
    // the color tween only reliably applied to "your own" heading. Heading
    // scale/color are now handled entirely by updateHeadings() below instead,
    // which is the single, authoritative place that touches both headings.
    tl.to(headingWrapper, { yPercent: 0 }, 0)
      // Widening the wrapper as it reveals means the heading (and the copy
      // block, which inherits available space from it) wrap less than they
      // would at the resting-state width. NOTE: we deliberately do NOT also
      // animate copyWrapper's own max-width -- that width feeds directly
      // into the natural-height measurement above, so if it changed mid-tween
      // the cached height target would go stale too.
      .to(wrapper, { padding: (window.innerWidth < 960 ? "0 2rem" : "0 4rem"), maxWidth: revealMaxWidth }, 0)
      .to(arrow, { opacity: 0 }, 0)
      .to(copyWrapper, { id: "copyHeight", opacity: 1, height: copyWrapperNaturalHeight }, 0)
      .to(colorBlock, { backgroundColor: accentColor }, 0)
      .to(revealImage, { scale: 1, rotation: 0, y: isLeftTrigger ? "10%" : "0%" }, 0)
      .to(revealAccent, { scale: isLeftTrigger ? .9 : 1, yPercent: isLeftTrigger ? -15 : 0 }, .05)
      .to(ingredientsWrapper, { scale: 1, opacity: .25 }, 0);

    // If the window is resized narrower AFTER this height was captured
    // (still within this same breakpoint's range, so matchMedia doesn't
    // rebuild anything), the cached target goes stale: less horizontal
    // room means the copy wraps onto more lines, but the tween keeps
    // animating to the old, now-too-short pixel height, clipping the
    // button. Re-measuring and pushing the fresh value onto the SAME
    // tween (via its id) keeps future reveals sized correctly without
    // rebuilding the whole timeline.
    function refreshCopyWrapperHeight() {
      copyWrapperNaturalHeight = measureCopyWrapperNaturalHeight();
      const heightTween = tl.getById("copyHeight");
      if (heightTween) {
        heightTween.vars.height = copyWrapperNaturalHeight;
        heightTween.invalidate();
      }
    }

    // Reveal ("in", target 1) and release ("out", target 0) get different
    // durations now that this is a toggle -- reveal-in slowed down per
    // request, release-out kept snappy. quickTo doesn't support different
    // durations per direction on one persistent tween, so this is a small
    // hand-rolled equivalent: a single tracked tween that's only killed and
    // recreated when the TARGET actually changes (not on every mousemove
    // call with the same target), which keeps it just as cheap as quickTo
    // was for the common case of the cursor sitting still on one side of
    // the toggle line.
    const revealInDuration = 0.6;
    const revealOutDuration = 0.25;
    let scrubTween = null;
    let scrubTarget = 0;
    const scrub = (target) => {
      if (target === scrubTarget && scrubTween) return;
      scrubTarget = target;
      if (scrubTween) scrubTween.kill();
      scrubTween = gsap.to(tl, {
        progress: target,
        duration: target === 1 ? revealInDuration : revealOutDuration,
        ease: "power2.out",
        overwrite: true
      });
    };

    timelines[side] = { tl, scrub, refreshHeight: refreshCopyWrapperHeight };
    triggerEls[side] = trigger;
    headingEls[side] = heading;

    trigger.addEventListener("focusin", () => setProgress(side, 1));
    trigger.addEventListener("focusout", () => setProgress(side, 0));
  });

  // Same "< 960" caveat as revealMaxWidth above: this whole block only runs
  // for the 620-959px range, so this isn't just for narrow phones.
  const headingGrowScale = window.innerWidth < 960 ? 2.5 / 1.5 : 2 / 1.5;
  const headingShrinkScale = window.innerWidth < 960 ? 1.5 / 1.5 : 1 / 1.5;

  const headingRestColor = "#414042";
  const headingActiveColor = "white";
  const lineRestColor = "#aece36";
  const lineActiveColor = "white";
  // tl's children (colorBlock, revealImage, copyWrapper, etc.) all share
  // tl's own default ease (power2.out) applied on top of tl's linear
  // progress -- so tl.progress()===0.5 does NOT mean those properties are
  // 50% blended, it means they're power2.out(0.5) blended. Reading lp/rp
  // straight into a LINEAR interpolate() for heading color/scale put those
  // on a different curve than everything else sharing the same tl, so they
  // visibly landed at different points for the same progress value (e.g.
  // background color already well underway while heading color lagged
  // behind). Running lp/rp through this same ease function before
  // interpolating keeps every property -- background, images, and heading
  // scale/color -- on the exact same curve.
  const tlEase = gsap.parseEase("power2.out");

  // The single source of truth for both headings' scale and color, and the
  // shared --line-color variable.
  //
  // Reads each side's LIVE tl.progress() rather than the raw, unsmoothed
  // sideProgress value. tl.progress() is being continuously eased toward
  // the mouse-driven target by entry.scrub's quickTo (power2.out, .25s) --
  // the exact same curve driving the wrapper/copy/image reveal -- so
  // reading it here means the heading scale/color track that identical
  // eased motion instead of jumping straight to the raw cursor position.
  // Previously this read sideProgress directly, which had no easing of its
  // own at all: the heading would snap immediately to wherever the mouse
  // was while the rest of the reveal was still smoothly catching up,
  // which is what made it feel disconnected from everything else.
  function updateHeadings() {
    const lp = timelines.left ? timelines.left.tl.progress() : 0;
    const rp = timelines.right ? timelines.right.tl.progress() : 0;
    // ROOT CAUSE FOUND: gsap.quickTo() targeting the "scale" transform
    // property on these h2 elements is what froze the page -- confirmed by
    // isolation, not just frequency/throttling (we tried direct calls,
    // gsap.ticker, and requestAnimationFrame coalescing; all froze with
    // quickTo). Swapping to a plain, immediate gsap.set() -- same element,
    // same property, no persistent tween object -- does not freeze.
    // transformOrigin set explicitly here (not left to the CSS
    // transform-origin rule) because GSAP's CSS plugin takes ownership of
    // transform-origin once it starts writing "scale" to an element, and can
    // override the CSS-authored value with its own default (center) --
    // left/right scaling from the bottom corner needs to be set through GSAP
    // directly to be reliable.
    //
    // Color is now interpolated directly from the same lp/rp progress used
    // for scale, instead of snapping at an anyActive threshold -- this
    // restores the original scrub-tied behavior (color eases in step with
    // the reveal, in both directions) from before that threshold logic was
    // introduced. It's safe to compute a fresh interpolated value every
    // frame here because gsap.set() is a plain, immediate style write, not
    // a tween -- there's no per-call tween-creation cost like gsap.to() had.
    const elp = lp > 0 ? tlEase(lp) : 0;
    const erp = rp > 0 ? tlEase(rp) : 0;
    gsap.set(headingEls.left, {
      transformOrigin: "left bottom",
      scale: lp > 0 ? gsap.utils.interpolate(1, headingGrowScale, elp)
        : rp > 0 ? gsap.utils.interpolate(1, headingShrinkScale, erp)
        : 1,
      color: lp > 0 ? gsap.utils.interpolate(headingRestColor, headingActiveColor, elp)
        : rp > 0 ? gsap.utils.interpolate(headingRestColor, headingActiveColor, erp)
        : headingRestColor
    });
    gsap.set(headingEls.right, {
      transformOrigin: "right bottom",
      scale: rp > 0 ? gsap.utils.interpolate(1, headingGrowScale, erp)
        : lp > 0 ? gsap.utils.interpolate(1, headingShrinkScale, elp)
        : 1,
      color: rp > 0 ? gsap.utils.interpolate(headingRestColor, headingActiveColor, erp)
        : lp > 0 ? gsap.utils.interpolate(headingRestColor, headingActiveColor, elp)
        : headingRestColor
    });
    const maxEasedProgress = Math.max(elp, erp);
    gsap.set("html", {
      "--line-color": maxEasedProgress > 0
        ? gsap.utils.interpolate(lineRestColor, lineActiveColor, maxEasedProgress)
        : lineRestColor
    });
  }

  // Zones are expressed as a fraction of that SIDE'S OWN .trigger element
  // width (the orange box in the debug overlay) rather than the whole hero,
  // so they visually line up with it instead of floating at some unrelated
  // fraction of the page. The mousemove listener still lives on #hero (below)
  // rather than on each trigger, so cross-side resets and the locked-zone
  // widening keep working even once the cursor leaves that element's box.
  //
  // - edge padding (outer ~35% of the trigger's width, ~60% once locked):
  //   right at the literal edge, doesn't toggle the animation at all --
  //   locking in grows THIS zone (eating into the dead zone), not the scrub
  //   zone, so it takes a deliberate move back toward center to release
  // - scrub zone: progress interpolates with cursor position here. Wider
  //   (baseScrubZoneRatio) on the way IN for a smoother approach; narrower
  //   (lockedScrubZoneRatio) on the way back OUT once locked, for a quicker release
  // - everything past that, toward the middle of the hero, is dead: nothing animates
  // Red (held/padding) zone set to exactly 65% of the trigger's width
  // before triggering, 85% once locked.
  const basePaddingRatio = 0.65;
  const lockedPaddingRatio = 0.85;
  const baseScrubZoneRatio = 0.30;
  const lockedScrubZoneRatio = 0.20;

  function setProgress(side, progress) {
    const entry = timelines[side];
    if (!entry) return;
    entry.scrub(progress);
    sideProgress[side] = progress;
    // IMPORTANT: "locked" should only change at the two extremes -- flip TRUE
    // once progress reaches 1, flip FALSE once it reaches 0 -- and stay put
    // for everything in between. Previously it unset the instant progress
    // dropped even slightly below 1, which meant the very first frame of
    // scrubbing OUT switched the zone math back to the narrower unlocked
    // ratios mid-exit. That put the cursor's current position outside that
    // now-much-smaller active zone, snapping progress straight to 0 instead
    // of scrubbing gradually through the (wider, locked) orange band.
    if (progress >= 1) {
      locked[side] = true;
    } else if (progress <= 0) {
      locked[side] = false;
    }
    if (progress > 0) {
      if (mvmt.paused()) { mvmt.play(); mvmt2.play(); }
      const other = side === "left" ? "right" : "left";
      if (timelines[other]) { timelines[other].tl.progress(0); locked[other] = false; sideProgress[other] = 0; }
    } else if (!locked.left && !locked.right) {
      mvmt.pause();
      mvmt2.pause();
    }
  }

  // Runs updateHeadings() every animation frame instead of once per
  // setProgress() call. Since updateHeadings now reads each side's LIVE
  // tl.progress() (see note above it), calling it only when the mouse moves
  // meant the heading would freeze at its last snapshot the moment the
  // cursor stopped, even while tl.progress() kept easing toward its target
  // for up to another .25s -- a visible desync right at the tail end of
  // every movement. A ticker callback keeps it continuously in sync
  // instead. This is safe to run every frame regardless of activity because
  // updateHeadings only calls gsap.set() (a plain, immediate style write),
  // never gsap.quickTo() -- quickTo was what caused the earlier freeze when
  // driven from a ticker; a bare gsap.set() read+write was confirmed safe
  // even at raw mousemove frequency.
  gsap.ticker.add(updateHeadings);

  function computeProgress(side, mouseX) {
    const el = triggerEls[side];
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const isLeftSide = side === "left";
    const distanceFromOuterEdge = isLeftSide ? (mouseX - rect.left) : (rect.right - mouseX);
    const isLocked = locked[side];
    const paddingRatio = isLocked ? lockedPaddingRatio : basePaddingRatio;
    const scrubZoneRatio = isLocked ? lockedScrubZoneRatio : baseScrubZoneRatio;
    const paddingZone = rect.width * paddingRatio;
    const scrubZone = rect.width * scrubZoneRatio;
    // TOGGLE instead of scrub: rather than continuously tracking cursor
    // position across the scrub zone, this now flips fully on/off the
    // instant the cursor crosses the MIDDLE of the scrub zone. The reveal
    // still animates smoothly (entry.scrub's quickTo eases toward whichever
    // target this returns over .25s) -- only the trigger point is now a
    // single line instead of a continuous range.
    //
    // Locked automatically gets more buffer before toggling out "for free"
    // here: paddingZone/scrubZone already switch to the wider
    // lockedPaddingRatio/lockedScrubZoneRatio above when isLocked is true,
    // so toggleThreshold shifts further from the edge (deeper into the
    // trigger) whenever a side is locked, exactly like the old scrub zone
    // did -- just collapsed to its midpoint instead of a range.
    const toggleThreshold = paddingZone + scrubZone / 2;
    return distanceFromOuterEdge < toggleThreshold ? 1 : 0;
  }

  const hero = document.querySelector("#hero");

  const handleHeroMouseMove = (e) => {
    setProgress("left", computeProgress("left", e.clientX));
    setProgress("right", computeProgress("right", e.clientX));
  };

  const handleHeroMouseLeave = () => {
    setProgress("left", 0);
    setProgress("right", 0);
  };

  hero.addEventListener("mousemove", handleHeroMouseMove);
  hero.addEventListener("mouseleave", handleHeroMouseLeave);

  // Keeps each trigger's cached copyWrapperNaturalHeight in sync if the
  // window is resized narrower/wider without crossing this breakpoint's
  // boundaries (matchMedia only tears this whole block down and rebuilds
  // it when the breakpoint itself changes, not on every resize within it).
  const handleResize = () => {
    Object.values(timelines).forEach((entry) => entry.refreshHeight && entry.refreshHeight());
  };
  window.addEventListener("resize", handleResize);

  // GSAP calls this automatically when this breakpoint's media query stops
  // matching (e.g. the window gets resized past it), so listeners and
  // timelines from the previous breakpoint don't linger and stack up.
  return () => {
    hero.removeEventListener("mousemove", handleHeroMouseMove);
    hero.removeEventListener("mouseleave", handleHeroMouseLeave);
    window.removeEventListener("resize", handleResize);
    gsap.ticker.remove(updateHeadings);
    mvmt.kill();
    mvmt2.kill();
    Object.values(timelines).forEach(({ tl }) => tl.kill());
  };


});

gsap.to(".section-image", {
  yPercent: 75,
  ease: "none",
  scrollTrigger: {
    trigger: "#hero",
    start: "top top",
    end: "bottom top",
    scrub: 1.5
  },
});
